import React, { useEffect, useRef, useState } from 'react';
import { func, number, object } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, injectIntl } from '../../../util/reactIntl';
import { constructQueryParamName } from '../../../util/search';
import { Icons } from '../../../components';

import {
  parseSelectedValues,
  formatFilterValue,
  searchFilterOptions,
  narrowToValues,
  typeaheadNoun,
} from './filterBarHelpers';
import useExceptFieldPresence from './useExceptFieldPresence';

import css from './FilterPill.module.css';

// Same rule as the panel: fields with more options than this get a type-ahead
const TYPE_AHEAD_THRESHOLD = 8;

/**
 * Head pill with in-place dropdown (ledger "Filter system — FINAL
 * 2026-08-25"): rest/hover/active/pressed states, "Label: Value" / "Label: +N"
 * active formats, Icons.js chevron caret (~10px, 130ms rotation), dropdown
 * card with "(pick any)" header, Clear, quiet-tint + check selected rows,
 * type-ahead above lists with >8 options, and a live green "Show N" footer.
 * Esc closes and returns focus to the pill; ↑/↓ move, Enter toggles.
 */
const FilterPillComponent = props => {
  const {
    config,
    urlQueryParams,
    facetsParams,
    getHandleChangedValueFn,
    totalItems,
    onOpenOverride,
    intl,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const rootRef = useRef(null);
  const pillRef = useRef(null);
  const optionsRef = useRef(null);

  const { key, scope = 'public', enumOptions = [], filterConfig = {} } = config;
  const label = filterConfig.label || key;
  const paramName = constructQueryParamName(key, scope);
  const selected = parseSelectedValues(config, urlQueryParams);
  const isActive = selected.length > 0;
  const isTypeAhead = enumOptions.length > TYPE_AHEAD_THRESHOLD;

  // Active-field exception: this field's list is computed against the OTHER
  // filters (facets minus its own param); null falls back to the full list
  const exceptPresence = useExceptFieldPresence(
    facetsParams || urlQueryParams,
    config,
    isOpen && isActive
  );

  const close = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDown = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    const onKey = e => {
      if (e.key === 'Escape') {
        close();
        pillRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  const submitValues = values =>
    getHandleChangedValueFn(true)({ [paramName]: formatFilterValue(config, values) });

  const toggleOption = optionKey => {
    const next = selected.includes(optionKey)
      ? selected.filter(k => k !== optionKey)
      : [...selected, optionKey];
    submitValues(next);
  };

  const clear = () => submitValues([]);

  const moveFocus = delta => {
    const items = [...(optionsRef.current?.querySelectorAll('button') || [])];
    const idx = items.indexOf(document.activeElement);
    const next = items[Math.min(items.length - 1, Math.max(0, idx + delta))];
    next?.focus();
  };

  const optionLabel = optionKey =>
    enumOptions.find(o => (o.option || o.key) === optionKey)?.label || optionKey;

  const pillText = !isActive
    ? label
    : selected.length === 1
    ? `${label}: ${optionLabel(selected[0])}`
    : `${label}: +${selected.length}`;

  const exceptNarrowed =
    isActive && exceptPresence ? narrowToValues(config, exceptPresence, selected) : config;
  const displayConfig = searchFilterOptions(config, searchTerm) || exceptNarrowed;

  return (
    <span className={css.root} ref={rootRef}>
      <button
        type="button"
        ref={pillRef}
        className={classNames(css.pill, {
          [css.pillActive]: isActive,
          [css.pillOpen]: isOpen,
        })}
        onClick={() =>
          onOpenOverride ? onOpenOverride() : isOpen ? close() : setIsOpen(true)
        }
      >
        {pillText}
        <span className={classNames(css.caret, { [css.caretOpen]: isOpen })}>
          <Icons name="downAngle" />
        </span>
      </button>

      {isOpen ? (
        <div
          className={css.dropdown}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              moveFocus(1);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              moveFocus(-1);
            }
          }}
        >
          <div className={css.dropdownHead}>
            <span className={css.dropdownLabel}>
              <FormattedMessage id="FilterPill.pickAny" values={{ label }} />
            </span>
            {isActive ? (
              <button type="button" className={css.clearButton} onClick={clear}>
                <FormattedMessage id="FilterPill.clear" />
              </button>
            ) : null}
            <button type="button" className={css.showAction} onClick={close}>
              {totalItems != null ? (
                <FormattedMessage id="FilterPill.showResults" values={{ count: totalItems }} />
              ) : (
                <FormattedMessage id="FilterPill.done" />
              )}
            </button>
          </div>
          {isTypeAhead ? (
            <input
              type="search"
              className={css.typeAheadInput}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={intl.formatMessage(
                { id: 'FilterPill.typeaheadPlaceholder' },
                { label: typeaheadNoun(label) }
              )}
            />
          ) : null}
          <div className={css.options} ref={optionsRef}>
            {(displayConfig.enumOptions || []).map(option => {
              const optionKey = option.option || option.key;
              const isSelected = selected.includes(optionKey);
              return (
                <button
                  type="button"
                  key={optionKey}
                  className={classNames(css.optionRow, { [css.optionSelected]: isSelected })}
                  onClick={() => toggleOption(optionKey)}
                >
                  <span className={css.optionCheck}>{isSelected ? '✓' : ''}</span>
                  <span className={css.optionLabel}>{option.label || optionKey}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </span>
  );
};

FilterPillComponent.defaultProps = {
  totalItems: null,
};

FilterPillComponent.propTypes = {
  config: object.isRequired,
  urlQueryParams: object.isRequired,
  getHandleChangedValueFn: func.isRequired,
  totalItems: number,
};

export default injectIntl(FilterPillComponent);
